/**
 * ⚠️⚠️ **WEB STORAGE CAN THROW, NOT JUST BE EMPTY — AND WHEN IT DID, THE PAGE
 * RENDERED NOTHING.**
 *
 * Chrome with "Block all cookies" (and some embedded / private contexts) throws
 * `SecurityError` from `window.localStorage` itself, or from `getItem` /
 * `setItem`. Measured on a built probe (2026-09-16):
 *   - `syncManager.ts` reads `sync-queue` in its constructor, at IMPORT time, so
 *     the entry module failed to evaluate: `#root` stayed empty, one uncaught
 *     SecurityError, no error boundary could help.
 *   - past that, every zustand `persist` store's `setItem` threw through `set()`
 *     on first render → "Something went wrong".
 *   - ~100 more call sites read and write storage without a try/catch.
 *
 * So instead of guarding every call site, this runs ONCE, before any other app
 * module (it must stay the FIRST import in `main.tsx` — `storageFallback.test.ts` pins that)
 * and, only when READING storage throws, installs an in-memory Storage on
 * `window`. Everything then works for the life of the tab and nothing persists
 * across a reload (the auth session included), which is the most a browser that
 * refuses storage allows.
 *
 * ⚠️ When storage works this does NOTHING: it never writes a probe key, never
 * wraps the real object. `window.localStorage` stays the browser's own.
 *
 * ⚠️ Detection is READ-ONLY on purpose. A `setItem` that throws QuotaExceeded on a
 * FULL but readable storage must not swap in an empty one — every store would
 * hydrate empty and the blob sync could then push that emptiness to the cloud.
 */

type StorageName = 'localStorage' | 'sessionStorage';

const STORAGE_NAMES: readonly StorageName[] = ['localStorage', 'sessionStorage'];
const PROBE_KEY = '__metanoia_storage_probe__';

/** A spec-shaped Storage backed by a Map. Not persisted anywhere. */
export function createMemoryStorage(): Storage {
  const items = new Map<string, string>();
  return {
    get length() {
      return items.size;
    },
    clear: () => items.clear(),
    getItem: (key: string) => {
      const value = items.get(String(key));
      return value === undefined ? null : value;
    },
    key: (index: number) => Array.from(items.keys())[index] ?? null,
    removeItem: (key: string) => {
      items.delete(String(key));
    },
    setItem: (key: string, value: string) => {
      items.set(String(key), String(value));
    },
  };
}

/** True when the storage can be reached and read without throwing. */
export function isStorageReadable(target: Window, name: StorageName): boolean {
  try {
    target[name].getItem(PROBE_KEY);
    return true;
  } catch {
    return false;
  }
}

/**
 * Swaps an in-memory Storage onto `target` for each storage whose read throws.
 * Returns the names it replaced (empty when storage works).
 */
export function installStorageFallback(target: Window = window): StorageName[] {
  const replaced: StorageName[] = [];
  for (const name of STORAGE_NAMES) {
    if (isStorageReadable(target, name)) continue;
    try {
      Object.defineProperty(target, name, {
        value: createMemoryStorage(),
        configurable: true,
        enumerable: true,
        writable: false,
      });
      replaced.push(name);
    } catch {
      // Not redefinable — nothing more can be done from here.
    }
  }
  if (replaced.length > 0) {
    console.warn(
      `Browser storage is blocked (${replaced.join(', ')}); using in-memory storage for this tab. Nothing will be kept after a reload.`,
    );
  }
  return replaced;
}

if (typeof window !== 'undefined') {
  installStorageFallback(window);
}
